import { Col, Row, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { Sample } from '../../services/sample.service';
import {
  formatSampleDateLong,
  formatSampleDateTimeLong,
} from '../../utils/dateFnsLocale';
import { formatSampleCreatorLabel } from '../../utils/name.util';
import { formatSampleTypeDisplay } from '../../utils/sampleTypeLabels';
import { getRecentUsageRecordsForDetail } from '../../utils/sampleUsageRecords';
import SampleDetailPathogenRows from './SampleDetailPathogenRows';
import SampleDetailSectionCard from './SampleDetailSectionCard';
import SampleDetailStorageCell from './SampleDetailStorageCell';
import SampleDetailUsageRecentList from './SampleDetailUsageRecentList';
import SampleFactRow from './SampleFactRow';

export type SampleDetailFactsPanelsProps = {
  sample: Sample;
  onViewAllHistory: () => void;
  showSampleId?: boolean;
  className?: string;
};

/** @deprecated 互換 re-export。新規は `SampleFactRow` を直接 import */
export { default as SampleFactRow } from './SampleFactRow';

/**
 * 詳細モーダル／フルページ共通の「基本・保管・使用履歴」パネル。
 * 使用記録の追加はヒーロー／モーダル操作帯が正（空帯に CTA を重ねない）。
 */
export default function SampleDetailFactsPanels({
  sample,
  onViewAllHistory,
  showSampleId = false,
  className,
}: SampleDetailFactsPanelsProps) {
  const { t, i18n } = useTranslation();
  const hasUsage = Boolean(sample.usageRecords && sample.usageRecords.length > 0);

  return (
    <div className={className}>
      <Row className="mb-3 g-3">
        <Col md={6}>
          <SampleDetailSectionCard
            iconClassName="fas fa-info-circle"
            title={t('sample.basic_info')}
          >
            <Table striped bordered size="sm" className="mb-0 sample-detail-facts-table">
              <tbody>
                {showSampleId ? (
                  <SampleFactRow label={t('common.id')}>{sample.id}</SampleFactRow>
                ) : null}
                <SampleFactRow label={t('sample.sample_type')}>
                  {formatSampleTypeDisplay(sample.sampleType, t)}
                </SampleFactRow>
                <SampleFactRow
                  label={t('sample.bsl_level_short', { defaultValue: 'BSL' })}
                  labelTitle={t('sample.bsl_level')}
                >
                  {sample.bslLevel || '-'}
                </SampleFactRow>
                <SampleFactRow
                  label={t('sample.creator', { defaultValue: '作製者' })}
                  labelTitle={t('sample.maker_name')}
                >
                  {formatSampleCreatorLabel(sample, i18n.language)}
                </SampleFactRow>
                <SampleFactRow label={t('sample.created_at')}>
                  {sample.createdAt
                    ? formatSampleDateTimeLong(sample.createdAt, i18n.language)
                    : '-'}
                </SampleFactRow>
                {sample.disposalDate ? (
                  <SampleFactRow label={t('sample.disposal_date')}>
                    {formatSampleDateLong(sample.disposalDate, i18n.language)}
                  </SampleFactRow>
                ) : null}
                <SampleDetailPathogenRows sample={sample} />
                {sample.notes ? (
                  <SampleFactRow label={t('sample.notes')}>
                    <span className="sample-detail-facts-prewrap">{sample.notes}</span>
                  </SampleFactRow>
                ) : null}
              </tbody>
            </Table>
          </SampleDetailSectionCard>
        </Col>
        <Col md={6}>
          <SampleDetailSectionCard
            iconClassName="fas fa-warehouse"
            title={t('sample.storage_info')}
          >
            <Table striped bordered size="sm" className="mb-0 sample-detail-facts-table">
              <tbody>
                <SampleFactRow label={t('sample.storage_room')}>
                  <SampleDetailStorageCell sample={sample} />
                </SampleFactRow>
                {sample.drawerNumber != null ? (
                  <SampleFactRow
                    label={t('sample.drawer_short', { defaultValue: '引き出し' })}
                    labelTitle={t('sample.drawer_number')}
                  >
                    {sample.drawerNumber}
                  </SampleFactRow>
                ) : null}
                {sample.caseName ? (
                  <SampleFactRow label={t('sample.case')}>{sample.caseName}</SampleFactRow>
                ) : null}
                {/* 残数量はヒーローバッジの正本。保管パネルでは重複させない */}
                {sample.storageDate ? (
                  <SampleFactRow label={t('sample.storage_date')}>
                    {formatSampleDateTimeLong(sample.storageDate, i18n.language)}
                  </SampleFactRow>
                ) : null}
              </tbody>
            </Table>
          </SampleDetailSectionCard>
        </Col>
      </Row>

      <SampleDetailSectionCard
        iconClassName="fas fa-history"
        title={t('sample.usage_records')}
        bodyClassName={hasUsage ? 'p-0 p-md-3' : undefined}
      >
        <div className={hasUsage ? 'px-3 py-2 px-md-0 py-md-0' : undefined}>
          <SampleDetailUsageRecentList
            {...(hasUsage
              ? getRecentUsageRecordsForDetail(
                  sample.usageRecords ?? [],
                  sample.initialQuantity
                )
              : { recentRecords: [], totalCount: 0 })}
            unit={sample.unit}
            onViewAllHistory={onViewAllHistory}
          />
        </div>
      </SampleDetailSectionCard>
    </div>
  );
}
